/**
 * Shared constants for the authenticate_person tool.
 * Must stay in sync with helpcare/relay/utils/auth_person/constants.py.
 * A CI smoke test in authConstants.test.ts validates this automatically.
 */

export const AUTH_FIELDS = [
  'first_name',
  'last_name',
  'full_name',
  'dob',
  'member_id',
  'mrn',
  'cin',
  'last4_member_id',
  'ssn',
  'last4_ssn',
  'address',
  'phone_number',
] as const

export type AuthField = (typeof AUTH_FIELDS)[number]

// Duty phone policy subset: relay/utils/auth_person/constants.py PHONE_VERIFY_FIELDS.
export const PHONE_VERIFY_FIELDS = [
  'dob',
  'member_id',
  'mrn',
  'cin',
  'last4_member_id',
  'ssn',
  'last4_ssn',
] as const satisfies readonly AuthField[]

// Independent from the phone-assisted policy; mirrors Relay's confirmation fields.
export const NUMERIC_CONFIRMATION_FIELDS = [
  'dob',
  'member_id',
  'mrn',
  'cin',
  'last4_member_id',
  'ssn',
  'last4_ssn',
] as const satisfies readonly AuthField[]

export const AUTH_FIELD_LABELS: Record<AuthField, string> = {
  first_name: 'First Name',
  last_name: 'Last Name',
  full_name: 'Full Name',
  dob: 'DOB',
  member_id: 'Member ID',
  mrn: 'MRN',
  cin: 'CIN',
  last4_member_id: 'Last 4 Member ID',
  ssn: 'SSN',
  last4_ssn: 'Last 4 SSN',
  address: 'Address',
  phone_number: 'Phone Number',
}

/**
 * Suggested person values for the `allowed_persons` list. Ad-hoc values are
 * still allowed via the custom-person input on the card; this list only
 * provides the well-known suggestions and their display labels.
 */
export const AUTH_PERSONS = [
  'self',
  'parent',
  'mother',
  'father',
  'legal_guardian',
  'attorney',
  'provider',
  'insurance_company',
  'family_member',
] as const

export type AuthPerson = (typeof AUTH_PERSONS)[number]

export const AUTH_PERSON_LABELS: Record<AuthPerson, string> = {
  self: 'Self',
  parent: 'Parent',
  mother: 'Mother',
  father: 'Father',
  legal_guardian: 'Legal guardian',
  attorney: 'Attorney',
  provider: 'Provider',
  insurance_company: 'Insurance company',
  family_member: 'Family member',
}

export const STATIC_DESCRIPTION =
  'Authenticate the speaker on this call. ' +
  'IMPORTANT: Call this tool first with `role` and `discover=true` to find out ' +
  'what fields are needed — do NOT guess or ask the caller for fields before ' +
  'calling the tool. The discovery response will include `ask_for` listing the ' +
  'field sets that satisfy auth. Then ask the caller for those fields and call ' +
  'the tool again with `role` and the identity fields (without `discover`). ' +
  'Returns AUTHENTICATED, or NOT_AUTHENTICATED ' +
  'with `ask_for` (remaining field sets that would satisfy auth, ordered from ' +
  'fewest to most fields — try the first option; if the caller cannot provide ' +
  'those fields, move to the next option), ' +
  '`retry` (provided fields that did not match and should be re-collected). ' +
  'Use `ask_for` to decide what to request next. ' +
  'Use `retry` only to re-collect values the caller already gave but that did ' +
  'not match. If `unsupported_fields` is returned, those arguments did not ' +
  'contribute to authentication; supported fields in the same request were ' +
  'still evaluated. Name accents are accepted. If `input_issues` reports an ' +
  'unsupported name script, follow its guidance to obtain the Latin-letter ' +
  'spelling used on the member record or card; do not invent a ' +
  'transliteration. An unsupported-script value does not replace retained ' +
  'evidence. Do not retry it if authentication succeeded or that field is ' +
  "already verified. `verified` lists fields retained from this call's earlier " +
  'attempts, and `candidate_count` reports whether those fields still match ' +
  'zero, one, or multiple patients. Do not re-ask for `verified` fields. The ' +
  'duty prompt decides whether another attempt is appropriate and what to do ' +
  'when authentication cannot be completed. Never claim authentication unless ' +
  'status is AUTHENTICATED. Always convert DOB to YYYY-MM-DD before calling ' +
  'this tool.'

export function renderDescription(allowedPersons: string[]): string {
  const personsList =
    allowedPersons.length > 0 ? allowedPersons.join(', ') : 'none'
  return `${STATIC_DESCRIPTION} Allowed persons for this duty: ${personsList}.`
}
